import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { buildTemplateUrl } from "./Api_url";
import { useSession } from "./SessionContext";
import { hasTelegramInitData } from "./telegramWebApp";
import { DeviceItem } from "../types/device";

export type ServiceItem = {
  user_service_id?: number;
  name?: string;
  status?: string;
  expire?: string;
  tarif?: number;
  tarifNext?: number;
  currentSort?: number;
  availableSorts?: number[];
  happLink?: string | null;
  subLink?: string | null;
  subLinkOther?: string | null;
  subLinkiOS?: string | null;
  subLinkQR?: string | null;
  subLinkThis?: string | null;
};

export type PriceItem = {
  tarif: number;
  name: string;
  description?: string;
  cost: number;
  period?: number;
  periodLabel?: string;
  visible?: boolean;
  deviceLimit?: number;
  months?: number;
  trafic?: string;
};

export type TariffPeriodItem = {
  tarif: number;
  months: number;
  cost: number;
  description?: string;
};

export type TariffPlanItem = {
  planCode: string;
  name: string;
  deviceLimit: number;
  trafficWhitelistGb: number;
  sort: number;
  periods: TariffPeriodItem[];
};

export type AppLinks = {
  androidApkLink: string | null;
  androidLink: string | null;
  appleTvLink: string | null;
  iosLinkGlobal: string | null;
  iosLinkRu: string | null;
  windowsLink: string | null;
};

type DevicesResponse = {
  totalDevice?: number;
  limitDevice?: number;
  itemsDevice?: DeviceItem[];
};

type RefreshProfileOptions = {
  silent?: boolean;
  preserveErrorView?: boolean;
};

type ServiceTariffUpdatePayload = {
  userServiceId: number;
  tarif: number | null;
  tarifNext: number | null;
  currentSort: number | null;
  availableSorts: number[];
};

type BonusItem = {
  date: string;
  bonus: number;
};

const normalizePaymentProviders = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim();
    return normalizedValue ? [normalizedValue] : [];
  }

  return [];
};

type ProfileData = {
  bonusHistory: BonusItem[];
  referralsCount: number;
  referralLink: string | null;
  referralWebLink: string | null;
  partnerPercent: number | null;
  needTelegramConnect: boolean;
  connectTelegramLink: string | null;

  userId: number | null;
  chatwootIdentifierHash: string | null;
  loginId: string | null;
  email: string | null;
  needEmailVerify: boolean;
  balance: number;
  discount: number;

  happLink: string | null;
  subLink: string | null;
  subLinkOther: string | null;
  subLinkiOS: string | null;
  subLinkQR: string | null;
  subLinkThis: string | null;
  payLink: string | null;
  ps: string[];
  supportLink: string | null;
  supportEmail: string | null;

  remindNeedTelegram: boolean;
  remindNeedInstall: boolean;

  next: number | null;
  needPay: boolean;

  services: ServiceItem[];
  itemsCount: number;

  trafficLimitTb: number;
  trafficUsedTb: number;

  devicesTotal: number;
  devicesLimit: number;
  devicesItems: DeviceItem[];

  tariffs: TariffPlanItem[];
  prices: PriceItem[];
  appLinks: AppLinks;
};

interface ProfileContextType extends ProfileData {
  loading: boolean;
  initialLoading: boolean;
  error: string | null;
  errorVersion: number;
  deletingHwid: string | null;
  deviceError: string | null;

  hasVisitedMain: boolean;
  markMainVisited: () => void;

  refreshProfile: (options?: RefreshProfileOptions) => Promise<boolean>;
  applyServiceTariffUpdate: (payload: ServiceTariffUpdatePayload) => void;
  getProfileSnapshot: () => ProfileData;
  deleteDevice: (hwid: string) => Promise<boolean>;
  clearDeviceError: () => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

const initialProfileData: ProfileData = {
  bonusHistory: [],
  referralsCount: 0,
  referralLink: null,
  referralWebLink: null,
  partnerPercent: null,
  needTelegramConnect: false,
  connectTelegramLink: null,

  userId: null,
  chatwootIdentifierHash: null,
  loginId: null,
  email: null,
  needEmailVerify: false,
  balance: 0,
  discount: 0,

  happLink: null,
  subLink: null,
  subLinkOther: null,
  subLinkiOS: null,
  subLinkQR: null,
  subLinkThis: null,
  payLink: null,
  ps: [],
  supportLink: null,
  supportEmail: null,

  remindNeedTelegram: false,
  remindNeedInstall: false,

  next: null,
  needPay: false,

  services: [],
  itemsCount: 0,

  trafficLimitTb: 0,
  trafficUsedTb: 0,

  devicesTotal: 0,
  devicesLimit: 0,
  devicesItems: [],

  tariffs: [],
  prices: [],
  appLinks: {
    androidApkLink: null,
    androidLink: null,
    appleTvLink: null,
    iosLinkGlobal: null,
    iosLinkRu: null,
    windowsLink: null,
  },
};

let sharedProfileRequest:
  | {
      sessionId: string;
      promise: Promise<any>;
    }
  | null = null;

const PROFILE_REQUEST_TIMEOUT_MS = 20000;
const PROFILE_RESUME_REFRESH_STALE_MS = 60000;
const PROFILE_RESUME_REFRESH_DEBOUNCE_MS = 10000;

const parseJsonWithTrailingCommaFallback = (rawText: string) => {
  try {
    return JSON.parse(rawText);
  } catch (error) {
    const sanitizedText = rawText.replace(/,\s*([}\]])/g, "$1");

    if (sanitizedText !== rawText) {
      return JSON.parse(sanitizedText);
    }

    throw error;
  }
};

const createProfileTimeoutError = () => {
  const error: Error & { status?: number } = new Error(
    "Истекло время ожидания ответа сервера"
  );
  error.status = 408;
  return error;
};

const fetchProfileData = async (sessionId: string) => {
  if (sharedProfileRequest?.sessionId === sessionId) {
    return sharedProfileRequest.promise;
  }

  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => {
    controller.abort();
  }, PROFILE_REQUEST_TIMEOUT_MS);

  const promise = fetch(buildTemplateUrl("profile3"), {
    headers: { "session-id": sessionId },
    signal: controller.signal,
  }).then(async (res) => {
    if (!res.ok) {
      const error: Error & { status?: number } = new Error(
        `Ошибка загрузки данных: ${res.status}`
      );
      error.status = res.status;
      throw error;
    }

    const responseText = await res.text();
    return parseJsonWithTrailingCommaFallback(responseText);
  }).catch((error: any) => {
    if (error?.name === "AbortError") {
      throw createProfileTimeoutError();
    }

    throw error;
  }).finally(() => {
    globalThis.clearTimeout(timeoutId);
  });

  sharedProfileRequest = { sessionId, promise };

  try {
    return await promise;
  } finally {
    if (sharedProfileRequest?.promise === promise) {
      sharedProfileRequest = null;
    }
  }
};

const normalizeLegacyPrices = (prices: any): PriceItem[] =>
  Array.isArray(prices)
    ? prices.map((item: any) => ({
        tarif: Number(item?.tarif ?? 0),
        name: String(item?.name ?? ""),
        description: typeof item?.description === "string" ? item.description : "",
        cost: Number(item?.cost ?? 0),
        period:
          item?.period !== undefined &&
          item?.period !== null &&
          !Number.isNaN(Number(item.period))
            ? Number(item.period)
            : Number(item?.months ?? 0),
        periodLabel: typeof item?.period === "string" ? item.period : "",
        visible: Number(item?.visible ?? 1) !== 0,
        deviceLimit: Number(item?.device_limit ?? item?.deviceLimit ?? 0),
        months: Number(item?.months ?? 0),
        trafic: String(item?.trafic ?? item?.traffic ?? "").trim(),
      }))
    : [];

const normalizeServices = (services: any): ServiceItem[] =>
  Array.isArray(services)
    ? services.map((item: any) => ({
        user_service_id:
          item?.user_service_id !== undefined && item?.user_service_id !== null
            ? Number(item.user_service_id)
            : undefined,
        name: typeof item?.name === "string" ? item.name : undefined,
        status: typeof item?.status === "string" ? item.status : undefined,
        expire: typeof item?.expire === "string" ? item.expire : undefined,
        tarif:
          item?.tarif !== undefined && item?.tarif !== null
            ? Number(item.tarif)
            : undefined,
        tarifNext:
          item?.tarifNext !== undefined && item?.tarifNext !== null
            ? Number(item.tarifNext)
            : undefined,
        currentSort:
          item?.currentSort !== undefined && item?.currentSort !== null
            ? Number(item.currentSort)
            : undefined,
        availableSorts: Array.isArray(item?.availableSorts)
          ? item.availableSorts
              .map((sort: any) => Number(sort))
              .filter((sort: number) => !Number.isNaN(sort))
          : undefined,
        happLink: typeof item?.happLink === "string" ? item.happLink : null,
        subLink: typeof item?.subLink === "string" ? item.subLink : null,
        subLinkOther:
          typeof item?.subLinkOther === "string" ? item.subLinkOther : null,
        subLinkiOS:
          typeof item?.subLinkiOS === "string" ? item.subLinkiOS : null,
        subLinkQR: typeof item?.subLinkQR === "string" ? item.subLinkQR : null,
        subLinkThis:
          typeof item?.subLinkThis === "string"
            ? item.subLinkThis
            : typeof item?.subLinkiOS === "string"
              ? item.subLinkiOS
              : null,
      }))
    : [];

const normalizeTariffPlans = (plans: any): TariffPlanItem[] =>
  Array.isArray(plans)
    ? plans
        .map((plan: any) => ({
          planCode: String(plan?.plan_code ?? plan?.planCode ?? "").trim(),
          name: String(plan?.name ?? "").trim(),
          deviceLimit: Number(plan?.device_limit ?? plan?.deviceLimit ?? 0),
          trafficWhitelistGb: Number(
            plan?.traffic_whitelist_gb ?? plan?.trafficWhitelistGb ?? 0
          ),
          sort: Number(plan?.sort ?? 0),
          periods: Array.isArray(plan?.periods)
            ? plan.periods
                .map((period: any) => ({
                  tarif: Number(period?.tarif ?? 0),
                  months: Number(period?.months ?? 0),
                  cost: Number(period?.cost ?? 0),
                  description:
                    typeof period?.description === "string"
                      ? period.description
                      : "",
                }))
                .filter((period: TariffPeriodItem) => period.tarif > 0)
                .sort((left: TariffPeriodItem, right: TariffPeriodItem) => {
                  const monthsDiff = left.months - right.months;

                  if (monthsDiff !== 0) {
                    return monthsDiff;
                  }

                  const costDiff = left.cost - right.cost;

                  if (costDiff !== 0) {
                    return costDiff;
                  }

                  return left.tarif - right.tarif;
                })
            : [],
        }))
        .filter((plan: TariffPlanItem) => plan.periods.length > 0)
        .sort((left: TariffPlanItem, right: TariffPlanItem) => {
          const sortDiff = left.sort - right.sort;

          if (sortDiff !== 0) {
            return sortDiff;
          }

          const deviceLimitDiff = left.deviceLimit - right.deviceLimit;

          if (deviceLimitDiff !== 0) {
            return deviceLimitDiff;
          }

          return left.name.localeCompare(right.name, "ru");
        })
    : [];

const normalizeDevicesItems = (devices: any): DeviceItem[] =>
  Array.isArray(devices)
    ? devices.map((device: any) => ({
        hwid: String(device?.hwid ?? ""),
        nameDevice: String(device?.nameDevice ?? ""),
        platformDevice: String(device?.platformDevice ?? ""),
        userAgent:
          typeof device?.userAgent === "string" ? device.userAgent : undefined,
        createdAt:
          typeof device?.createdAt === "string" ? device.createdAt : undefined,
      }))
    : [];

const mapPlansToPrices = (plans: TariffPlanItem[]): PriceItem[] =>
  plans.flatMap((plan) =>
    plan.periods.map((period) => ({
      tarif: period.tarif,
      name: plan.name,
      description: period.description ?? "",
      cost: period.cost,
      period: period.months,
      periodLabel: "",
      visible: true,
      deviceLimit: plan.deviceLimit,
      months: period.months,
      trafic: String(plan.trafficWhitelistGb),
    }))
  );

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    sessionId,
    isAuthenticated,
    logout,
    reauthorizeWithTelegram,
    authorizedFetch,
  } =
    useSession();
  const [hasVisitedMain, setHasVisitedMain] = useState(false);
  const [profile, setProfile] = useState<ProfileData>(initialProfileData);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorVersion, setErrorVersion] = useState(0);
  const [deletingHwid, setDeletingHwid] = useState<string | null>(null);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  const hasLoadedOnceRef = useRef(false);
  const profileRef = useRef<ProfileData>(initialProfileData);
  const lastProfileSyncAtRef = useRef(0);
  const lastResumeRefreshAtRef = useRef(0);

  const applyDevicesData = useCallback((devices?: DevicesResponse | null) => {
    return {
      devicesTotal: Number(devices?.totalDevice ?? 0),
      devicesLimit: Number(devices?.limitDevice ?? 0),
      devicesItems: Array.isArray(devices?.itemsDevice)
        ? normalizeDevicesItems(devices?.itemsDevice)
        : [],
    };
  }, []);

  const applyProfilePayload = useCallback(
    (data: any) => {
      const servicesArr = normalizeServices(data.user_service);

      const firstService = servicesArr[0] ?? null;
      const tariffsArr = normalizeTariffPlans(data?.tariffs?.plans);
      const pricesArr =
        tariffsArr.length > 0
          ? mapPlansToPrices(tariffsArr)
          : normalizeLegacyPrices(data.prices);

      const bonusesArr: BonusItem[] = Array.isArray(data.last_bonuses)
        ? data.last_bonuses.map((item: any) => ({
            date: String(item?.date ?? ""),
            bonus: Number(item?.bonus ?? 0),
          }))
        : [];

      const appLinksRaw = data.appLink ?? {};

      const nextProfile: ProfileData = {
        bonusHistory: bonusesArr,
        referralsCount: Number(data.referrals_count ?? 0),
        referralLink: data.referral_link ?? null,
        referralWebLink: data.referral_link_web ?? null,
        partnerPercent:
          data.partner_percent !== undefined && data.partner_percent !== null
            ? Number(data.partner_percent)
            : null,
        needTelegramConnect: Boolean(Number(data.needTelegramConnect ?? 0)),
        connectTelegramLink: data.connectTelegramLink ?? null,

        userId:
          data.user_id !== undefined && data.user_id !== null
            ? Number(data.user_id)
            : null,
        chatwootIdentifierHash:
          typeof data.chatwoot_identifier_hash === "string"
            ? data.chatwoot_identifier_hash.trim() || null
            : null,
        loginId: typeof data.login === "string" ? data.login : null,
        email: typeof data.email === "string" ? data.email : null,
        needEmailVerify: Boolean(Number(data.needEmailVerify ?? 0)),
        balance: Number(data.balance ?? 0),
        discount: Number(data.discount ?? 0),

        happLink: firstService?.happLink ?? null,
        subLink: firstService?.subLink ?? null,
        subLinkOther: firstService?.subLinkOther ?? null,
        subLinkiOS: firstService?.subLinkiOS ?? null,
        subLinkQR: firstService?.subLinkQR ?? null,
        subLinkThis:
          firstService?.subLinkThis ?? firstService?.subLinkiOS ?? null,
        payLink: typeof data.payLink === "string" ? data.payLink : null,
        ps: normalizePaymentProviders(data.ps),
        supportLink: data.supportLink ?? null,
        supportEmail: data.supportEmail ?? null,

        remindNeedTelegram: Boolean(Number(data?.remind?.needTelegram ?? 0)),
        remindNeedInstall: Boolean(Number(data?.remind?.needInstall ?? 0)),

        next:
          firstService?.tarifNext !== undefined &&
          firstService?.tarifNext !== null
            ? Number(firstService.tarifNext)
            : firstService?.tarif !== undefined && firstService?.tarif !== null
              ? Number(firstService.tarif)
              : null,

        needPay: ["BLOCK", "DISABLED", "EXPIRED"].includes(
          String(firstService?.status ?? "").toUpperCase()
        ),

        services: servicesArr,
        itemsCount: Number(data.services ?? 0),

        trafficLimitTb: Number(data.traffic?.limit_tb ?? 0),
        trafficUsedTb: Number(data.traffic?.used_tb ?? 0),

        ...applyDevicesData(data.devices),

        tariffs: tariffsArr,
        prices: pricesArr,
        appLinks: {
          androidApkLink: appLinksRaw.androidApkLink ?? null,
          androidLink: appLinksRaw.androidLink ?? null,
          appleTvLink: appLinksRaw.appleTvLink ?? null,
          iosLinkGlobal: appLinksRaw.iosLinkGlobal ?? null,
          iosLinkRu: appLinksRaw.iosLinkRu ?? null,
          windowsLink: appLinksRaw.windowsLink ?? null,
        },
      };

      profileRef.current = nextProfile;
      lastProfileSyncAtRef.current = Date.now();
      setProfile(nextProfile);

      setError(null);
      hasLoadedOnceRef.current = true;
    },
    [applyDevicesData]
  );

  const loadProfileBySession = useCallback(
    async (targetSessionId: string) => {
      const data = await fetchProfileData(targetSessionId);
      applyProfilePayload(data);
      return true;
    },
    [applyProfilePayload]
  );

  const refreshProfile = useCallback(
    async (options: RefreshProfileOptions = {}): Promise<boolean> => {
      const { silent = false, preserveErrorView = false } = options;

      if (!sessionId) return false;

      if (!silent) {
        if (!hasLoadedOnceRef.current && !preserveErrorView) {
          setInitialLoading(true);
        } else {
          setLoading(true);
        }
      }

      try {
        return await loadProfileBySession(sessionId);
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          if (hasTelegramInitData()) {
            const refreshedSessionId = await reauthorizeWithTelegram();

            if (refreshedSessionId) {
              try {
                return await loadProfileBySession(refreshedSessionId);
              } catch (retryErr: any) {
                if (retryErr?.status !== 401 && retryErr?.status !== 403) {
                  if (!silent) {
                    const retryMessage =
                      retryErr?.message || "Ошибка загрузки данных";
                    setError(retryMessage);
                    setErrorVersion((prev) => prev + 1);
                  }

                  return false;
                }
              }
            }
          }

          logout("session_expired");
          return false;
        }

        if (!silent) {
          const message = err?.message || "Ошибка загрузки данных";
          setError(message);
          setErrorVersion((prev) => prev + 1);
        }

        return false;
      } finally {
        setInitialLoading(false);
        setLoading(false);
      }
    },
    [sessionId, loadProfileBySession, logout, reauthorizeWithTelegram]
  );

  const applyServiceTariffUpdate = useCallback(
    ({
      userServiceId,
      tarif,
      tarifNext,
      currentSort,
      availableSorts,
    }: ServiceTariffUpdatePayload) => {
      const normalizedAvailableSorts = Array.isArray(availableSorts)
        ? availableSorts
            .map((sort) => Number(sort))
            .filter((sort, index, list) => !Number.isNaN(sort) && list.indexOf(sort) === index)
        : [];

      setProfile((prev) => {
        const nextServices = prev.services.map((service) => {
          if (Number(service.user_service_id ?? 0) !== userServiceId) {
            return service;
          }

          return {
            ...service,
            tarif: tarif ?? service.tarif,
            tarifNext: tarifNext ?? tarif ?? service.tarifNext,
            currentSort: currentSort ?? service.currentSort,
            availableSorts: normalizedAvailableSorts,
          };
        });

        const nextFirstService = nextServices[0] ?? null;
        const nextTarifValue =
          nextFirstService?.tarifNext !== undefined &&
          nextFirstService?.tarifNext !== null
            ? Number(nextFirstService.tarifNext)
            : nextFirstService?.tarif !== undefined &&
                nextFirstService?.tarif !== null
              ? Number(nextFirstService.tarif)
              : null;

        const nextProfile = {
          ...prev,
          services: nextServices,
          next: nextTarifValue,
        };

        profileRef.current = nextProfile;
        return nextProfile;
      });
    },
    []
  );

  const deleteDevice = useCallback(
    async (hwid: string) => {
      if (!sessionId || !hwid) return false;

      try {
        setDeletingHwid(hwid);
        setDeviceError(null);

        const res = await authorizedFetch(
          buildTemplateUrl(`devicedel1?hwid=${encodeURIComponent(hwid)}`),
          {
            method: "GET",
          }
        );

        if (!res.ok) {
          throw new Error(`Ошибка удаления устройства: ${res.status}`);
        }

        const data = await res.json();

        if (!data.ok) {
          setDeviceError(data.message || data.error || "Ошибка удаления");
          return false;
        }

        setProfile((prev) => ({
          ...prev,
          ...applyDevicesData(data.devices),
        }));
        return true;
      } catch (err: any) {
        setDeviceError(err?.message || "Ошибка при удалении устройства");
        return false;
      } finally {
        setDeletingHwid(null);
      }
    },
    [sessionId, applyDevicesData, authorizedFetch]
  );

  const clearDeviceError = useCallback(() => {
    setDeviceError(null);
  }, []);

  const getProfileSnapshot = useCallback(() => {
    return profileRef.current;
  }, []);

  const refreshProfileOnResume = useCallback(() => {
    if (!isAuthenticated || !sessionId || initialLoading || loading) {
      return;
    }

    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      return;
    }

    const currentTimestamp = Date.now();

    if (
      currentTimestamp - lastResumeRefreshAtRef.current <
      PROFILE_RESUME_REFRESH_DEBOUNCE_MS
    ) {
      return;
    }

    if (
      lastProfileSyncAtRef.current > 0 &&
      currentTimestamp - lastProfileSyncAtRef.current <
        PROFILE_RESUME_REFRESH_STALE_MS
    ) {
      return;
    }

    lastResumeRefreshAtRef.current = currentTimestamp;
    void refreshProfile({
      silent: true,
      preserveErrorView: true,
    });
  }, [initialLoading, isAuthenticated, loading, refreshProfile, sessionId]);

  const markMainVisited = useCallback(() => {
    setHasVisitedMain(true);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !sessionId) {
      profileRef.current = initialProfileData;
      lastProfileSyncAtRef.current = 0;
      lastResumeRefreshAtRef.current = 0;
      setProfile(initialProfileData);
      setInitialLoading(true);
      setLoading(false);
      setError(null);
      setErrorVersion(0);
      setDeviceError(null);
      setDeletingHwid(null);
      setHasVisitedMain(false);
      hasLoadedOnceRef.current = false;
      return;
    }

    refreshProfile();
  }, [isAuthenticated, sessionId, refreshProfile]);

  useEffect(() => {
    if (!isAuthenticated || !sessionId) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshProfileOnResume();
      }
    };

    const handleFocus = () => {
      refreshProfileOnResume();
    };

    const handlePageShow = () => {
      refreshProfileOnResume();
    };

    const handleOnline = () => {
      refreshProfileOnResume();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("online", handleOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("online", handleOnline);
    };
  }, [isAuthenticated, refreshProfileOnResume, sessionId]);

  useEffect(() => {
    if (!deviceError) return;

    const t = setTimeout(() => {
      setDeviceError(null);
    }, 3000);

    return () => clearTimeout(t);
  }, [deviceError]);

  const value = useMemo(
    () => ({
      ...profile,
      loading,
      initialLoading,
      error,
      errorVersion,
      deletingHwid,
      deviceError,
      hasVisitedMain,
      markMainVisited,
      refreshProfile,
      applyServiceTariffUpdate,
      getProfileSnapshot,
      deleteDevice,
      clearDeviceError,
    }),
    [
      profile,
      loading,
      initialLoading,
      error,
      errorVersion,
      deletingHwid,
      deviceError,
      hasVisitedMain,
      markMainVisited,
      refreshProfile,
      applyServiceTariffUpdate,
      getProfileSnapshot,
      deleteDevice,
      clearDeviceError,
    ]
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};

export const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);

  if (!context) {
    throw new Error("useProfile must be used within ProfileProvider");
  }

  return context;
};
